import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';

// =============================================================================
// PAGE TRANSITION WRAPPER
// =============================================================================

type TransitionType = 'fade' | 'slide' | 'scale' | 'slideUp' | 'slideDown';

interface PageTransitionProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  type?: TransitionType;
  duration?: number;
}

const transitionVariants: Record<TransitionType, Variants> = {
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  slide: {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  },
  scale: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 1.05 },
  },
  slideUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  },
  slideDown: {
    initial: { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
  },
};

export const PageTransition: React.FC<PageTransitionProps> = ({
  children,
  className,
  style,
  type = 'fade',
  duration = 0.3,
}) => {
  const variants = transitionVariants[type];

  return (
    <motion.div
      className={className}
      style={style}
      initial="initial"
      animate="animate"
      exit="exit"
      variants={variants}
      transition={{ duration, ease: 'easeInOut' }}
    >
      {children}
    </motion.div>
  );
};

// =============================================================================
// SLIDE TRANSITION (Horizontal page switch)
// =============================================================================

interface SlideTransitionProps {
  children: React.ReactNode;
  direction?: 'left' | 'right';
  className?: string;
  style?: React.CSSProperties;
}

export const SlideTransition: React.FC<SlideTransitionProps> = ({
  children,
  direction = 'left',
  className,
  style,
}) => {
  const xOffset = direction === 'left' ? 100 : -100;

  return (
    <motion.div
      className={className}
      style={style}
      initial={{ opacity: 0, x: xOffset }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -xOffset }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
      }}
    >
      {children}
    </motion.div>
  );
};

// =============================================================================
// FADE SCALE TRANSITION (Modal-like)
// =============================================================================

interface FadeScaleTransitionProps {
  children: React.ReactNode;
  isVisible: boolean;
  className?: string;
  style?: React.CSSProperties;
  origin?: 'center' | 'top' | 'bottom';
}

export const FadeScaleTransition: React.FC<FadeScaleTransitionProps> = ({
  children,
  isVisible,
  className,
  style,
  origin = 'center',
}) => {
  const originMap = {
    center: 'center center',
    top: 'center top',
    bottom: 'center bottom',
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={className}
          style={{
            transformOrigin: originMap[origin],
            ...style,
          }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25,
          }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// PAGE TRANSITION CONTEXT
// =============================================================================

interface PageTransitionContextType {
  isTransitioning: boolean;
  transitionType: TransitionType;
  setTransitionType: (type: TransitionType) => void;
  triggerTransition: (callback: () => void) => void;
}

const PageTransitionContext = createContext<PageTransitionContextType | null>(null);

export const PageTransitionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionType, setTransitionType] = useState<TransitionType>('fade');

  const triggerTransition = useCallback((callback: () => void) => {
    setIsTransitioning(true);
    setTimeout(() => {
      callback();
      setTimeout(() => setIsTransitioning(false), 300);
    }, 200);
  }, []);

  return (
    <PageTransitionContext.Provider
      value={{
        isTransitioning,
        transitionType,
        setTransitionType,
        triggerTransition,
      }}
    >
      {children}
    </PageTransitionContext.Provider>
  );
};

export const usePageTransition = () => {
  const context = useContext(PageTransitionContext);
  if (!context) {
    // Return default values if not in provider
    return {
      isTransitioning: false,
      transitionType: 'fade' as TransitionType,
      setTransitionType: () => {},
      triggerTransition: (cb: () => void) => cb(),
    };
  }
  return context;
};

// =============================================================================
// ANIMATED ROUTE WRAPPER
// =============================================================================

interface AnimatedRouteProps {
  children: React.ReactNode;
  routeKey: string;
  className?: string;
}

export const AnimatedRoute: React.FC<AnimatedRouteProps> = ({
  children,
  routeKey,
  className,
}) => {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={routeKey}
        className={className}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.2 }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

// =============================================================================
// STAGGERED PAGE CONTENT
// =============================================================================

interface StaggeredPageContentProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  staggerDelay?: number;
}

export const StaggeredPageContent: React.FC<StaggeredPageContentProps> = ({
  children,
  className,
  style,
  staggerDelay = 0.1,
}) => {
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    },
  };

  return (
    <motion.div
      className={className}
      style={style}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {React.Children.map(children, (child, index) => (
        <motion.div key={index} variants={itemVariants}>
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
};

export default {
  PageTransition,
  SlideTransition,
  FadeScaleTransition,
  PageTransitionProvider,
  usePageTransition,
  AnimatedRoute,
  StaggeredPageContent,
};
